import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, InputSignal, OnDestroy, ViewEncapsulation, computed, effect, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, Subject, debounceTime, lastValueFrom } from 'rxjs';
import { IPHXEditComponent } from '../../../@core/interfaces/IPHXEditComponent.interface';
import { OpenStockViewSumSearchInput, Product, StoragePlace } from '../../../generated/generated-types';
import { OpenStockViewSumService } from "../../../modules/warehouse/services/open-stock-view-sum.service";

@Component({
  selector: 'app-phx-input-quantity-with-warehouse',
  templateUrl: './phx-input-quantity-with-warehouse.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    FormsModule,
    InputTextModule,
  ]
})
export class PhxInputQuantityWithWarehouseComponent implements IPHXEditComponent, OnDestroy {

  ngModelChange = output<number>();

  ngModelChange$ = new BehaviorSubject<number>(null);

  ngModel = model<number>();

  destroy$ = new Subject<void>();

  disabled = model(false);

  showWarehouseStockButton = input<boolean>(false);

  product: InputSignal<Product> = input<Product>(null);
  productId: InputSignal<string> = input<string>(null);

  storagePlace = input<StoragePlace>(null);
  storagePlaceId: InputSignal<string> = input<string>(null);

  serial: InputSignal<string> = input<string>(null);
  lotNumber: InputSignal<string> = input<string>(null);

  quantityInWarehouse = signal<number>(null);
  isLoading = signal(false);

  /** Article unit from master data. Empty until a product with a unit is selected. */
  readonly unitIdentifier = computed(() => this.product()?.unit?.identifier?.trim() ?? '');
  lastSearchInput: OpenStockViewSumSearchInput = {};

  searchTrigger = new Subject<void>();

  dataTestId = model<string>();

  constructor(
    private openStockViewSumService: OpenStockViewSumService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.searchTrigger.pipe(debounceTime(700)).subscribe(() =>
      this.checkQuantityBackedByStock()
    );

    effect(() => {
      this.product();
      this.productId();
      this.storagePlace();
      this.storagePlaceId();
      this.serial();
      this.lotNumber();

      let productId = this.product()?.id ?? this.productId()
      let storagePlaceId = this.storagePlace()?.id ?? this.storagePlaceId()

      if (!productId || !storagePlaceId) {
        this.quantityInWarehouse.set(null);
        this.isLoading.set(false);
        return;
      }
      else if ((this.lastSearchInput?.productIds?.[0]) != productId || (this.lastSearchInput?.storagePlaceIds?.[0]) != storagePlaceId) {
        this.isLoading.set(true);
        this.searchTrigger.next();
      }
    });
  }

  onValueChange(e: number) {
    this.ngModelChange.emit(e);
    this.ngModelChange$.next(e);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async checkQuantityBackedByStock() {
    this.isLoading.set(true);

    let productId = this.product()?.id ?? this.productId()
    let storagePlaceId = this.storagePlace()?.id ?? this.storagePlaceId()

    if (!productId || !storagePlaceId) {
      this.quantityInWarehouse.set(null);
      this.isLoading.set(false);
      return;
    }

    let input = {
      groupProduct: true,
      groupStoragePlace: true,

      groupLot: (this.lotNumber()?.length ?? -1) > 0,
      groupSerial: (this.serial()?.length ?? -1) > 0,

      productIds: [productId],
      storagePlaceIds: [storagePlaceId],
    } as OpenStockViewSumSearchInput;

    // if (JSON.stringify(input) === JSON.stringify(this.lastSearchInput)) {
    //   this.isLoading = false;
    //   return;
    // }
    let stock = (await lastValueFrom(this.openStockViewSumService.getData(input)))?.result;

    if (stock && stock.length > 0) {
      if (this.lotNumber() && (!this.product() || this.product()?.inventoryTrackingType == "LOT"))
        stock = stock?.filter(x => x.lotNumber == this.lotNumber());
      if (this.serial() && (!this.product() || this.product()?.inventoryTrackingType == "SERIAL"))
        stock = stock?.filter(x => x.serialNumber == this.serial());
    }

    this.quantityInWarehouse.set(stock[0]?.quantity ?? 0);
    this.isLoading.set(false);
    this.lastSearchInput = input;
  }

  emitQuantityInWarehouse() {
    this.ngModel.set(this.quantityInWarehouse());
    this.ngModelChange.emit(this.ngModel());
  }
}
